import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { TitleAndContentFieldsMui } from '@app/modules/social-export/components/inputs/TitleAndContentFieldsMui';
import { PublishFormUnsavedChangesWarning } from '@app/modules/social-export/components/PublishFormUnsavedChangesWarning';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, Stack } from '@mui/material';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { RudoPublishDto, RudoSettingsDto } from '../../social-export.types';
import { RudoFoldersField } from './folder-field/RudoFoldersField';
import { RudoRestrictionField } from './RudoRestrictionField';
import {
  RudoPublishFormValues,
  RudoPublishParsedValues,
  useRudoPublishSchema,
} from './useRudoPublishSchema';

type RudoPublishFormProps = BaseExportFormProps<RudoPublishDto, RudoSettingsDto>;

/**
 * Publish form for Rudo: title, description, tags, thumbnail, folder, and VOD restriction.
 */
export function RudoPublishForm({
  source,
  onUpload,
  defaultSettings,
  connectionId,
  videoUrl,
  actions,
}: RudoPublishFormProps) {
  const { $t } = useIntl();
  const schema = useRudoPublishSchema();
  const initialValues: RudoPublishFormValues = {
    title: source.title ?? '',
    content: source.description ?? '',
    tags: source.tags.join(', '),
    folderId: defaultSettings?.folderId ?? null,
    restriction: (defaultSettings?.restriction == null
      ? '0'
      : String(defaultSettings.restriction)) as '0' | '1' | '2',
    thumbType: 'DEFAULT',
    thumbnailUrl: null,
    thumbOffsetMs: null,
  };

  const beforeSubmit = (data: RudoPublishParsedValues): RudoPublishDto => ({
    title: data.title,
    description: data.content.trim() || undefined,
    tags: data.tags
      ? data.tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    folderId: data.folderId,
    restriction: data.restriction,
    thumbnailUrl:
      data.thumbType === 'IMAGE_URL' || data.thumbType === 'VIDEO_FRAME'
        ? (data.thumbnailUrl ?? undefined)
        : undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData as RudoPublishParsedValues));
      }}
    >
      <PublishFormUnsavedChangesWarning />
      <ContentCluster
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <Stack spacing={4}>
          <TitleAndContentFieldsMui />
          <Grid container>
            <Grid item md={8}>
              <TextFieldElement
                name="tags"
                label={$t({
                  id: 'socialExport.form.tags',
                  defaultMessage: 'Tags',
                })}
                helperText={$t({
                  id: 'socialExport.form.commaSeparatedValues',
                  defaultMessage: 'Valores separados por coma',
                })}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item md={10}>
              <ThumbFieldsMui showFrameImageToField videoUrl={videoUrl} />
            </Grid>
          </Grid>
        </Stack>
      </ContentCluster>
      <ContentCluster
        title={$t({
          id: 'socialExport.form.settings',
          defaultMessage: 'Parámetros de publicación',
        })}
      >
        <Stack spacing={4}>
          <Grid container>
            <Grid item md={8}>
              <RudoFoldersField connectionId={connectionId} required />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item md={8}>
              <RudoRestrictionField />
            </Grid>
          </Grid>
        </Stack>
      </ContentCluster>
      {actions}
    </FormContainer>
  );
}
